const HEADER_NAVIGATION_BREAKPOINT = 1200;
/* A media query instead of window.innerWidth: on phones (meta viewport) Chrome brings style and layout
   of the whole page up to date for every innerWidth read, so each burger tap, each resize and each
   sticky-header scroll frame paid a forced layout first, and so did the header's start-up while the page
   was still loading. Same breakpoint as header-navigation-desktop.css (min-width: 1200px), and like
   innerWidth it counts the scrollbar. */
const headerNavigationDesktopQuery = window.matchMedia(`(min-width: ${HEADER_NAVIGATION_BREAKPOINT}px)`);
const isHeaderNavigationDesktopWidth = () => headerNavigationDesktopQuery.matches;
const isHeaderNavigationDesktop = (root) =>
  isHeaderNavigationDesktopWidth() && !root.classList.contains("is-mobile-layout");
const HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS = 220;
let headerNavigationScrollY = 0;

/* Runs `callback` once the browser has painted the next frame. A tap's visible change (the drawer
   sliding in or out) goes out first; page-wide work the shopper does not see at once (the root class
   that ui-overlay-system.css restyles most of the page for, the scroll lock, the focus move) runs in a
   later task instead of holding that paint back (INP). rAF does not run in a hidden tab, so a timer
   makes sure the work is never left half done. */
const afterHeaderNavigationPaint = (callback) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };
  window.requestAnimationFrame(() => window.setTimeout(run, 0));
  window.setTimeout(run, 250);
};

/* Mega-menu product rails. The header HTML carries no product cards (about 130 cards on every page
   cost too much Liquid time), so each rail is its collection's ?view=header-navigation-products
   render. Until Nov 2026 every desktop page view requested all ~24 rails (the default ones while the
   page loaded, the rest after load), refreshed every 5 minutes, whether or not the menu was used:
   about 24 storefront renders per desktop shopper per 5 minutes. Now (Cyber Week load):
   - nothing is requested until the shopper shows intent: the pointer (mouse, pen or touch) or keyboard
     focus entering the header. Then each category's default rail is requested, two at a time at low
     priority; opening a category requests its own rails first, and the rail on screen jumps the queue
     (urgent). Rails of categories never opened are never requested;
   - the cards are kept in localStorage per shopper context (data-rails-context: market, language,
     currency, launch menus, customer tags and VIP; plus the theme and the locale root) for up to
     60 minutes, and never across the top of a UTC hour, so a price change made on the hour (a sale
     opening) shows in the menu by then;
   - each rail's cards go into its hidden panel as soon as they are there, and their images load
     when the shopper heads for the menu (default rails) or opens a category (all of its rails),
     so moving between subcategories only switches `hidden`. */
const HEADER_NAVIGATION_RAILS_STORAGE_KEY = "hb:mega-rails:v1";
const HEADER_NAVIGATION_RAILS_TTL = 60 * 60 * 1000;
const HEADER_NAVIGATION_RAILS_CONCURRENCY = 2;
const HEADER_NAVIGATION_HOUR_MS = 60 * 60 * 1000;
// Stored rails are fresh while younger than the TTL and still in the UTC hour they were stored in.
const isHeaderNavigationRailsAgeFresh = (storedAt, now = Date.now()) => {
  const age = now - Number(storedAt);
  if (!(age >= 0 && age < HEADER_NAVIGATION_RAILS_TTL)) return false;
  return Math.floor(Number(storedAt) / HEADER_NAVIGATION_HOUR_MS) === Math.floor(now / HEADER_NAVIGATION_HOUR_MS);
};

const headerNavigationRails = {
  context: "",
  storedAt: 0,
  markup: new Map(),
  requests: new Map(),
  queue: [],
  running: 0,
  saveTimer: null,
};

const getHeaderNavigationRailUrl = (panel) => {
  if (!panel?.dataset.productsUrl) return "";
  try {
    const url = new URL(panel.dataset.productsUrl, window.location.href);
    url.search = "view=header-navigation-products";
    url.hash = "";
    return url.href;
  } catch (_) {
    return "";
  }
};

const whenHeaderNavigationIdle = (callback) => {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout: 500 });
  } else {
    window.setTimeout(callback, 50);
  }
};

const initHeaderNavigationRailsStore = (context) => {
  const rails = headerNavigationRails;
  rails.context = context;
  rails.storedAt = Date.now();

  try {
    const stored = JSON.parse(window.localStorage.getItem(HEADER_NAVIGATION_RAILS_STORAGE_KEY));
    if (stored?.context !== context || !isHeaderNavigationRailsAgeFresh(stored?.at)) return;

    rails.storedAt = stored.at;
    Object.entries(stored.rails || {}).forEach(([url, markup]) => {
      if (typeof markup === "string") rails.markup.set(url, markup);
    });
  } catch (_) {
    // No storage (private mode, blocked): rails are requested on each page as before.
  }
};

const saveHeaderNavigationRails = () => {
  const rails = headerNavigationRails;
  window.clearTimeout(rails.saveTimer);
  rails.saveTimer = null;
  if (!rails.context || !rails.markup.size) return;
  // A page left open past the hour starts a new store, so its rails do not carry the old hour's age forward.
  if (!isHeaderNavigationRailsAgeFresh(rails.storedAt)) return;

  try {
    window.localStorage.setItem(
      HEADER_NAVIGATION_RAILS_STORAGE_KEY,
      // The oldest rail sets the age, so no rail outlives the TTL.
      JSON.stringify({ context: rails.context, at: rails.storedAt, rails: Object.fromEntries(rails.markup) })
    );
  } catch (_) {
    // Quota or blocked storage: the in-memory rails still serve this page.
  }
};

const scheduleHeaderNavigationRailsSave = () => {
  window.clearTimeout(headerNavigationRails.saveTimer);
  headerNavigationRails.saveTimer = window.setTimeout(saveHeaderNavigationRails, 300);
};

window.addEventListener("pagehide", () => {
  if (headerNavigationRails.saveTimer) saveHeaderNavigationRails();
});

const runHeaderNavigationRailRequest = (entry, priority) => {
  const rails = headerNavigationRails;
  entry.started = true;
  rails.running += 1;

  fetch(entry.url, { credentials: "same-origin", priority })
    .then((response) => {
      if (!response?.ok) throw new Error("Header navigation products request failed.");
      return response.text();
    })
    .then((text) => {
      // Keep the cards only (whitespace collapsed): an error or challenge page never reaches the menu.
      const template = document.createElement("template");
      template.innerHTML = text;
      const markup = Array.from(
        template.content.querySelectorAll(".header-navigation-product-card"),
        (card) => card.outerHTML.replace(/\s{2,}/g, " ")
      ).join("");
      rails.markup.set(entry.url, markup);
      rails.requests.delete(entry.url);
      scheduleHeaderNavigationRailsSave();
      entry.resolve(markup);
    })
    .catch(() => {
      // Forget the failed request so the next open of the rail tries again.
      rails.requests.delete(entry.url);
      entry.resolve(null);
    })
    .finally(() => {
      rails.running -= 1;
      pumpHeaderNavigationRails();
    });
};

const pumpHeaderNavigationRails = () => {
  const rails = headerNavigationRails;
  while (rails.running < HEADER_NAVIGATION_RAILS_CONCURRENCY && rails.queue.length) {
    const entry = rails.requests.get(rails.queue.shift());
    if (entry && !entry.started) runHeaderNavigationRailRequest(entry, "low");
  }
};

// Resolves with the rail's card markup, or null when the request failed. `urgent` skips the queue
// (the rail is on screen); `first` moves a queued rail to the front (its category was opened).
const requestHeaderNavigationRail = (url, { urgent = false, first = false } = {}) => {
  const rails = headerNavigationRails;
  if (!url) return Promise.resolve(null);
  if (rails.markup.has(url)) return Promise.resolve(rails.markup.get(url));

  let entry = rails.requests.get(url);
  if (!entry) {
    entry = { url, started: false };
    entry.promise = new Promise((resolve) => {
      entry.resolve = resolve;
    });
    rails.requests.set(url, entry);
    rails.queue.push(url);
  }

  if (!entry.started) {
    if (urgent) {
      runHeaderNavigationRailRequest(entry, "high");
    } else {
      if (first) rails.queue = [url, ...rails.queue.filter((queued) => queued !== url)];
      pumpHeaderNavigationRails();
    }
  }
  return entry.promise;
};

// Puts the rail's cards in its panel once the markup is there; true when the panel has its cards.
const fillHeaderNavigationRail = (panel) => {
  if (!panel) return false;
  if (panel.dataset.productsLoaded !== "true") {
    const markup = headerNavigationRails.markup.get(getHeaderNavigationRailUrl(panel));
    const strip = panel.querySelector(".header-navigation__mega-products-strip");
    if (markup === undefined || !strip) return false;

    const template = document.createElement("template");
    template.innerHTML = markup;
    strip.replaceChildren(template.content);
    strip.removeAttribute("aria-busy");
    panel.dataset.productsLoaded = "true";
  }

  // Lazy images in a hidden panel only load once it shows; a rail the shopper is about to see loads them now.
  if (panel.dataset.railImages === "eager") {
    panel.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = "eager";
    });
  }
  return true;
};

// `images`: the shopper is heading for this rail, so its cards go in now (or on arrival) with their
// images loading; otherwise the cards go in when the browser is idle, images left lazy.
const warmHeaderNavigationRail = (panel, { images = false, first = false } = {}) => {
  const url = getHeaderNavigationRailUrl(panel);
  if (!url) return;
  if (images) {
    panel.dataset.railImages = "eager";
    if (fillHeaderNavigationRail(panel)) return;
  }

  requestHeaderNavigationRail(url, { first }).then((markup) => {
    if (markup === null) return;
    if (images) {
      fillHeaderNavigationRail(panel);
    } else {
      whenHeaderNavigationIdle(() => fillHeaderNavigationRail(panel));
    }
  });
};

// The rail on screen: cards at once when the markup is there, otherwise the placeholder until it arrives.
const loadHeaderNavigationProducts = (panel) => {
  const url = getHeaderNavigationRailUrl(panel);
  if (!url) return;
  panel.dataset.railImages = "eager";
  if (fillHeaderNavigationRail(panel)) return;

  const strip = panel.querySelector(".header-navigation__mega-products-strip");
  strip?.setAttribute("aria-busy", "true");
  requestHeaderNavigationRail(url, { urgent: true }).then((markup) => {
    if (markup === null) {
      strip?.removeAttribute("aria-busy");
      return;
    }
    fillHeaderNavigationRail(panel);
  });
};

const lockHeaderNavigationScroll = () => {
  /* Overflow-only lock: fixing the body visually jumped the page and
     un-stuck the sticky header (see the same change in details-modal.js). */
  headerNavigationScrollY = window.scrollY;
  document.body.style.overflow = "hidden";
};

const unlockHeaderNavigationScroll = () => {
  document.body.style.overflow = "";
  window.scrollTo(0, headerNavigationScrollY);
};

const initHeaderNavigationDesktop = (root) => {
  const subnav = root.querySelector("[data-desktop-subnav]");
  const primaryPanels = Array.from(root.querySelectorAll("[data-primary-panel]"));
  const secondaryNavLinks = Array.from(root.querySelectorAll(".header-navigation__secondary-link"));
  const secondaryLinks = Array.from(root.querySelectorAll("[data-secondary-trigger]"));
  const secondaryPanels = Array.from(root.querySelectorAll("[data-secondary-panel]"));
  const megaLinkTriggers = Array.from(root.querySelectorAll("[data-mega-link-trigger]"));
  const submenuStatus = subnav?.querySelector("[data-desktop-submenu-status]");

  if (!subnav || !primaryPanels.length) {
    return {
      closeAll: () => {},
    };
  }

  const defaultPrimary = root.dataset.defaultPrimary || primaryPanels[0]?.dataset.primaryPanel || "";
  const state = {
    activeSecondary: "",
    secondaryCloseTimer: null,
  };

  const getPanelFocusableElements = (panel) => {
    if (typeof getFocusableElements === "function") {
      return getFocusableElements(panel).filter((element) => !element.hasAttribute("disabled"));
    }

    return Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"])')
    );
  };

  const getSecondaryTrigger = (secondaryId) =>
    root.querySelector(`[data-secondary-trigger="${secondaryId}"]`);

  const getSecondaryPanel = (secondaryId) =>
    root.querySelector(`[data-secondary-panel="${secondaryId}"]`);

  const announceSubmenu = (message) => {
    if (!submenuStatus || !message) return;
    submenuStatus.textContent = "";
    window.requestAnimationFrame(() => {
      submenuStatus.textContent = message;
    });
  };

  const getSubmenuOpenedMessage = (trigger) => {
    const title =
      trigger?.querySelector(".header-navigation__secondary-link-label")?.textContent?.trim() ||
      trigger?.textContent?.trim() ||
      "";
    const template = subnav.dataset.submenuOpenedTemplate || "{{ title }} menu opened";
    return template.replace("__TITLE__", title).replace("{{ title }}", title);
  };

  const syncPrimary = () => {
    subnav.classList.remove("is-hidden");

    primaryPanels.forEach((panel) => {
      panel.hidden = panel.dataset.primaryPanel !== defaultPrimary;
    });
  };

  const clearSecondaryCloseTimer = () => {
    if (state.secondaryCloseTimer) {
      window.clearTimeout(state.secondaryCloseTimer);
      state.secondaryCloseTimer = null;
    }
  };

  const syncMegaPanel = (panel) => {
    if (!panel) return;

    const activeTertiary = panel.dataset.activeTertiary || panel.dataset.defaultTertiary || "";
    const triggers = Array.from(panel.querySelectorAll("[data-mega-link-trigger]"));
    const productPanels = Array.from(panel.querySelectorAll("[data-mega-products-panel]"));

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.megaLinkTrigger === activeTertiary;
      trigger.classList.toggle("is-highlighted", isActive);
      if (trigger.hasAttribute("aria-controls")) {
        trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
      }
    });

    productPanels.forEach((productPanel) => {
      const isActive = productPanel.dataset.megaProductsPanel === activeTertiary;
      productPanel.hidden = !isActive;

      if (isActive) {
        loadHeaderNavigationProducts(productPanel);
      }
    });
  };

  const activateMegaPanelLink = (tertiaryId) => {
    if (!isHeaderNavigationDesktop(root) || !tertiaryId) return;

    const megaPanel = root.querySelector(`[data-mega-link-trigger="${tertiaryId}"]`)?.closest("[data-secondary-panel]");
    if (!megaPanel) return;

    megaPanel.dataset.activeTertiary = tertiaryId;
    syncMegaPanel(megaPanel);
  };

  const syncSecondary = () => {
    document.documentElement.classList.toggle("hb-mega-menu-open", Boolean(state.activeSecondary));

    // The search band sits above the mega panel in the header's stacking
    // order, so a menu opened while search is up was hidden behind it.
    if (state.activeSecondary) {
      document.querySelectorAll("details-modal.header__search").forEach((search) => {
        if (typeof search.isOpen === "function" && search.isOpen()) search.close(false);
      });
    }

    secondaryLinks.forEach((link) => {
      const isActive = link.dataset.secondaryTrigger === state.activeSecondary;
      link.classList.toggle("is-active", isActive);
      link.setAttribute("aria-expanded", isActive ? "true" : "false");
    });

    secondaryPanels.forEach((panel) => {
      const isActive = panel.dataset.secondaryPanel === state.activeSecondary;
      panel.hidden = !isActive;

      if (!isActive) return;

      if (!panel.dataset.activeTertiary && panel.dataset.defaultTertiary) {
        panel.dataset.activeTertiary = panel.dataset.defaultTertiary;
      }

      syncMegaPanel(panel);
    });
  };

  const clearSecondary = ({ announce = false } = {}) => {
    const wasOpen = Boolean(state.activeSecondary);
    clearSecondaryCloseTimer();
    state.activeSecondary = "";
    syncSecondary();

    if (announce && wasOpen) {
      announceSubmenu(subnav.dataset.submenuClosed || "Menu closed");
    }
  };

  /* Product rails (see headerNavigationRails at the top): nothing is requested while the page loads.
     The pointer or keyboard focus entering the header requests each category's default rail (low
     priority, two at a time) and loads its images; opening a category requests and loads all of its
     rails, ahead of the queue. With Save-Data the default rails wait for a category to be opened. */
  initHeaderNavigationRailsStore(
    [root.dataset.railsContext || "", window.Shopify?.theme?.id || "", window.Shopify?.routes?.root || "/"].join("|")
  );
  const getRailPanels = (panel) => Array.from(panel.querySelectorAll("[data-mega-products-panel][data-products-url]"));
  const defaultRailPanels = secondaryPanels
    .map((panel) => panel.querySelector(`[data-mega-products-panel="${panel.dataset.defaultTertiary}"][data-products-url]`))
    .filter(Boolean);

  // Stored rails go into their panels now (no request), so a menu opened later shows them at once.
  secondaryPanels.flatMap(getRailPanels).forEach((panel) => {
    if (headerNavigationRails.markup.has(getHeaderNavigationRailUrl(panel))) {
      whenHeaderNavigationIdle(() => fillHeaderNavigationRail(panel));
    }
  });

  let defaultRailImagesWarmed = false;
  const warmDefaultRailImages = () => {
    if (defaultRailImagesWarmed || !isHeaderNavigationDesktop(root)) return;
    defaultRailImagesWarmed = true;
    if (navigator.connection?.saveData) return;
    defaultRailPanels.forEach((panel) => warmHeaderNavigationRail(panel, { images: true }));
  };

  // pointerenter covers mouse, pen and touch (a touch-capable desktop fires it before the tap); focusin
  // covers the keyboard. Both stay bound until the first one fires.
  const onHeaderIntent = () => {
    warmDefaultRailImages();
    if (!defaultRailImagesWarmed) return;
    root.removeEventListener("pointerenter", onHeaderIntent);
    root.removeEventListener("focusin", onHeaderIntent);
  };
  root.addEventListener("pointerenter", onHeaderIntent);
  root.addEventListener("focusin", onHeaderIntent);

  const activateSecondary = (secondaryId, { announce = false } = {}) => {
    if (!isHeaderNavigationDesktop(root) || !secondaryId) return;

    const alreadyOpen = state.activeSecondary === secondaryId;
    const panel = getSecondaryPanel(secondaryId);
    clearSecondaryCloseTimer();
    if (!alreadyOpen && panel) panel.dataset.activeTertiary = panel.dataset.defaultTertiary || "";
    state.activeSecondary = secondaryId;
    syncSecondary();
    warmDefaultRailImages();
    if (!alreadyOpen && panel) {
      getRailPanels(panel).forEach((railPanel) => warmHeaderNavigationRail(railPanel, { images: true, first: true }));
    }

    if (announce && !alreadyOpen) {
      announceSubmenu(getSubmenuOpenedMessage(getSecondaryTrigger(secondaryId)));
    }
  };

  const scheduleSecondaryClose = () => {
    if (!isHeaderNavigationDesktop(root) || !state.activeSecondary) return;

    clearSecondaryCloseTimer();
    state.secondaryCloseTimer = window.setTimeout(() => {
      state.secondaryCloseTimer = null;
      clearSecondary();
    }, 120);
  };

  const closeAll = () => {
    syncPrimary();
    clearSecondary();
  };

  const isDesktopNav = () => isHeaderNavigationDesktop(root);

  const getAdjacentItem = (items, current, offset) => {
    const index = items.indexOf(current);
    if (index < 0) return null;
    return items[index + offset] || null;
  };

  const moveFocus = (event, element, { stop = false } = {}) => {
    if (!element) return false;
    event.preventDefault();
    if (stop) event.stopPropagation();
    element.focus();
    return true;
  };

  const getSecondaryRailLinks = (fromEl) => {
    const primaryPanel = fromEl?.closest("[data-primary-panel]");
    return primaryPanel
      ? Array.from(primaryPanel.querySelectorAll(".header-navigation__secondary-link"))
      : secondaryNavLinks;
  };

  const getMegaRailTriggers = (panel) =>
    Array.from(panel.querySelectorAll("[data-mega-link-trigger]"));

  const getMegaCta = (panel) => panel.querySelector(".header-navigation__mega-cta");

  const getProductPanelForMegaLink = (trigger) => {
    const panelId = trigger?.getAttribute("aria-controls");
    return panelId ? document.getElementById(panelId) : null;
  };

  const getProductFocusables = (productPanel) => {
    if (!productPanel || productPanel.hidden) return [];
    return getPanelFocusableElements(productPanel);
  };

  const getMegaLinkProduct = (trigger, which = "first") => {
    const tertiaryId = trigger?.dataset.megaLinkTrigger;
    if (!trigger?.hasAttribute("aria-controls") || !tertiaryId) return null;

    activateMegaPanelLink(tertiaryId);
    const focusables = getProductFocusables(getProductPanelForMegaLink(trigger));
    if (!focusables.length) return null;
    return which === "last" ? focusables[focusables.length - 1] : focusables[0];
  };

  const getNextSecondaryAfterPanel = (panel) =>
    getAdjacentItem(
      getSecondaryRailLinks(panel),
      getSecondaryTrigger(panel.dataset.secondaryPanel),
      1
    );

  const focusAfterMegaPanel = (event, panel) => {
    if (moveFocus(event, getNextSecondaryAfterPanel(panel))) return;

    const focusables =
      typeof getFocusableElements === "function"
        ? getFocusableElements(document.body)
        : getPanelFocusableElements(document.body);
    const activeIndex = focusables.indexOf(document.activeElement);

    for (let index = activeIndex + 1; index < focusables.length; index += 1) {
      if (!panel.contains(focusables[index]) && moveFocus(event, focusables[index])) return;
    }
  };

  secondaryNavLinks.forEach((link) => {
    const secondaryId = link.dataset.secondaryTrigger;
    const handleEnter = (event) => {
      if (secondaryId) {
        const openedByKeyboard = event?.type === "focus" && link.matches(":focus-visible");
        activateSecondary(secondaryId, { announce: openedByKeyboard });
      } else {
        clearSecondary();
      }
    };

    link.addEventListener("mouseenter", handleEnter);
    link.addEventListener("focus", handleEnter);
    link.addEventListener("click", (event) => {
      if (!secondaryId) return;
      if (link.tagName === "A" && link.getAttribute("href")) return;

      event.preventDefault();
      activateSecondary(secondaryId);
    });

    link.addEventListener("keydown", (event) => {
      if (!isDesktopNav()) return;

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const nextLink = getAdjacentItem(
          getSecondaryRailLinks(link),
          link,
          (event.key === "ArrowRight" ? 1 : -1) * (getComputedStyle(root).direction === "rtl" ? -1 : 1)
        );
        moveFocus(event, nextLink);
        return;
      }

      if (!secondaryId || event.key !== "Tab" || event.shiftKey) return;
      if (link.getAttribute("aria-expanded") !== "true") return;

      const panel = getSecondaryPanel(secondaryId);
      if (!panel || panel.hidden) return;

      moveFocus(event, getPanelFocusableElements(panel)[0]);
    });
  });

  megaLinkTriggers.forEach((trigger) => {
    const tertiaryId = trigger.dataset.megaLinkTrigger;
    if (!tertiaryId) return;

    const activate = () => activateMegaPanelLink(tertiaryId);
    trigger.addEventListener("mouseenter", activate);
    trigger.addEventListener("focus", activate);

    trigger.addEventListener("keydown", (event) => {
      if (!isDesktopNav()) return;

      const megaPanel = trigger.closest("[data-secondary-panel]");
      const railTriggers = megaPanel ? getMegaRailTriggers(megaPanel) : [];

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        moveFocus(
          event,
          getAdjacentItem(railTriggers, trigger, event.key === "ArrowDown" ? 1 : -1)
        );
        return;
      }

      const forwardKey = getComputedStyle(root).direction === "rtl" ? "ArrowLeft" : "ArrowRight";
      if (event.key !== forwardKey && !(event.key === "Tab" && !event.shiftKey)) return;
      moveFocus(event, getMegaLinkProduct(trigger, "first"));
    });
  });

  secondaryPanels.forEach((panel) => {
    panel.addEventListener("keydown", (event) => {
      if (event.key !== "Tab" || !event.shiftKey) return;
      if (document.activeElement?.closest("[data-mega-products-panel]")) return;

      const focusables = getPanelFocusableElements(panel);
      if (!focusables.length || document.activeElement !== focusables[0]) return;

      moveFocus(event, getSecondaryTrigger(panel.dataset.secondaryPanel));
    });

    panel.querySelectorAll("[data-mega-products-panel]").forEach((productPanel) => {
      productPanel.addEventListener("keydown", (event) => {
        if (event.key !== "Tab") return;

        const productFocusables = getProductFocusables(productPanel);
        if (!productFocusables.length) return;

        const megaLink = panel.querySelector(
          `[data-mega-link-trigger="${productPanel.dataset.megaProductsPanel}"]`
        );
        const railTriggers = getMegaRailTriggers(panel);

        if (event.shiftKey) {
          if (document.activeElement !== productFocusables[0]) return;
          moveFocus(event, megaLink, { stop: true });
          return;
        }

        if (document.activeElement !== productFocusables[productFocusables.length - 1]) return;

        const nextTarget =
          getAdjacentItem(railTriggers, megaLink, 1) ||
          getMegaCta(panel) ||
          getNextSecondaryAfterPanel(panel);

        moveFocus(event, nextTarget, { stop: true });
      });
    });

    const cta = getMegaCta(panel);
    if (!cta) return;

    cta.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;

      if (event.shiftKey) {
        const railTriggers = getMegaRailTriggers(panel);
        for (let index = railTriggers.length - 1; index >= 0; index -= 1) {
          if (moveFocus(event, getMegaLinkProduct(railTriggers[index], "last"))) return;
        }
        return;
      }

      focusAfterMegaPanel(event, panel);
    });
  });

  primaryPanels.forEach((panelGroup) => {
    panelGroup.addEventListener("mouseenter", () => {
      clearSecondaryCloseTimer();
    });

    panelGroup.addEventListener("mouseleave", (event) => {
      if (!isDesktopNav()) return;
      if (event.relatedTarget instanceof Node && panelGroup.contains(event.relatedTarget)) return;

      scheduleSecondaryClose();
    });

    panelGroup.addEventListener("focusin", () => {
      clearSecondaryCloseTimer();
    });

    panelGroup.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (panelGroup.contains(document.activeElement)) return;

        clearSecondary();
      }, 0);
    });
  });

  document.addEventListener("pointerdown", (event) => {
    if (!isDesktopNav()) return;
    if (!root.contains(event.target)) {
      closeAll();
    }
  });

  document.addEventListener("keydown", (event) => {
    if ((event.code || event.key || "").toUpperCase() !== "ESCAPE" || !isDesktopNav()) return;

    const openSecondaryId = state.activeSecondary;
    if (!openSecondaryId) {
      closeAll();
      return;
    }

    const trigger = getSecondaryTrigger(openSecondaryId);
    event.preventDefault();
    clearSecondary({ announce: true });
    trigger?.focus();
  });

  window.addEventListener("resize", syncPrimary);

  syncPrimary();

  return { closeAll };
};

const initHeaderNavigationSticky = (root, closeDesktop) => {
  const section = root.closest(".section-header-navigation");
  const topbar = root.querySelector(".header-navigation__topbar");

  if (!section || !topbar) return;

  let isStickyCompact = false;
  let expandedHeight = 0;
  let ticking = false;

  const measureExpandedLayout = () => {
    const wasStickyCompact = root.classList.contains("is-sticky-compact");

    if (wasStickyCompact) {
      root.classList.remove("is-sticky-compact");
      section.classList.remove("is-sticky-active");
    }

    expandedHeight = root.offsetHeight;

    if (wasStickyCompact) {
      root.classList.add("is-sticky-compact");
      section.classList.add("is-sticky-active");
    }
  };

  const getStickyThreshold = () => {
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    return sectionTop + expandedHeight;
  };

  /* The compact bar shares its row with the logo and the icons. Where the
     labels don't fit at the default gap (narrow laptops, longer translations,
     markets with extra menu items), narrow the gap and then the type, so the
     first item and its NEW badge never slide under the logo. */
  const COMPACT_GAP_MAX = 36;
  const COMPACT_GAP_MIN = 12;
  const COMPACT_EDGE_BREATHING = 8;

  const fitCompactNav = () => {
    root.style.removeProperty("--header-navigation-compact-gap");
    root.classList.remove("is-sticky-compact-tight");
    if (!isStickyCompact) return;

    const nav = Array.from(root.querySelectorAll(".header-navigation__secondary-nav")).find((el) => el.offsetParent);
    const list = nav?.querySelector(".header-navigation__secondary-list");
    const itemCount = list?.children.length || 0;
    if (itemCount < 2) return;

    const spareGap = () => {
      const navStyle = getComputedStyle(nav);
      const available = nav.clientWidth - parseFloat(navStyle.paddingLeft) - parseFloat(navStyle.paddingRight);
      // Sum the items: the list itself is a flex item that shrinks to the space,
      // so its own width understates what the labels need.
      const content = Array.from(list.children).reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
      const room = available - COMPACT_EDGE_BREATHING * 2;
      return Math.floor((room - content) / (itemCount - 1));
    };

    let gap = spareGap();
    if (gap < COMPACT_GAP_MIN) {
      root.classList.add("is-sticky-compact-tight");
      gap = spareGap();
    }
    root.style.setProperty("--header-navigation-compact-gap", `${Math.max(COMPACT_GAP_MIN, Math.min(COMPACT_GAP_MAX, gap))}px`);
  };

  const applyStickyState = (shouldStick) => {
    if (isStickyCompact === shouldStick) return;

    isStickyCompact = shouldStick;
    root.classList.toggle("is-sticky-compact", shouldStick);
    section.classList.toggle("is-sticky-active", shouldStick);
    fitCompactNav();
    closeDesktop();
  };

  const syncStickyState = () => {
    ticking = false;

    if (!isHeaderNavigationDesktop(root)) {
      applyStickyState(false);
      section.style.minHeight = "";
      return;
    }

    if (!expandedHeight) {
      measureExpandedLayout();
    }

    section.style.minHeight = `${expandedHeight}px`;
    applyStickyState(window.scrollY > getStickyThreshold());
  };

  const syncStickyMetrics = () => {
    if (!isHeaderNavigationDesktop(root)) {
      expandedHeight = 0;
      section.style.minHeight = "";
      applyStickyState(false);
      return;
    }

    measureExpandedLayout();
    section.style.minHeight = `${expandedHeight}px`;
    syncStickyState();
    fitCompactNav();
  };

  const requestSyncStickyState = () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(syncStickyState);
  };

  syncStickyMetrics();

  window.addEventListener("scroll", requestSyncStickyState, { passive: true });
  document.fonts?.ready.then(fitCompactNav);
  return syncStickyMetrics;
};

const openHeaderNavigationSearchModal = (root, trigger) => {
  const searchSummary = root.querySelector(".header-navigation__search-modal-host .header__icon--search");

  if (!searchSummary) {
    const fallbackUrl = trigger?.getAttribute("href");
    if (fallbackUrl) window.location.href = fallbackUrl;
    return;
  }

  const modal = searchSummary.closest('details-modal');
  if (modal) {
    modal.returnFocusElement = trigger?.closest('[data-mobile-drawer]')
      ? root.querySelector('[data-mobile-drawer-toggle]')
      : trigger;
  }

  /* The open search renders as a fixed band anchored below the visible
     header (so the header row stays on screen above it). Measure where the
     header ends right now: the sticky/compact states change its height. When
     the header is off screen, fall back to the viewport top. The variable
     lives on the search host (every rule that reads it is inside it), and it
     is only written when it changes: a per-frame write on <html> invalidated
     style for the whole page on every frame while the search was open. */
  const searchHost = root.querySelector(".header-navigation__search-modal-host") || document.documentElement;
  const syncSearchBarTop = () => {
    const headerBottom = Math.round(root.getBoundingClientRect().bottom);
    const barTop = headerBottom > 0 && headerBottom < window.innerHeight / 2 ? headerBottom : 0;
    const value = `${barTop}px`;
    if (searchHost.style.getPropertyValue("--hb-search-bar-top") === value) return;
    searchHost.style.setProperty("--hb-search-bar-top", value);
  };

  syncSearchBarTop();

  /* Keep the band glued to the header for as long as the search stays open.
     A per-frame sync (instead of scroll/resize listeners) also tracks states
     that fire no window scroll event: the sticky header's grow/shrink
     animation, and the on-screen keyboard shifting the visual viewport on
     phones. The loop retires itself once the search closes. */
  if (!root.__hbSearchBarSyncActive) {
    root.__hbSearchBarSyncActive = true;
    const tick = () => {
      if (!root.querySelector(".header-navigation__search-modal-host details[open]")) {
        root.__hbSearchBarSyncActive = false;
        return;
      }
      syncSearchBarTop();
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  searchSummary.click();
};

const initHeaderNavigationSearch = (root, closeDesktop) => {
  const searchTriggers = Array.from(root.querySelectorAll(".header-navigation__topbar [data-header-navigation-search-trigger]"));

  searchTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      closeDesktop();
      /* Let the trigger click finish bubbling before DetailsModal attaches its
         outside-click listener; otherwise that same click immediately closes
         the search again. */
      window.setTimeout(() => openHeaderNavigationSearchModal(root, trigger), 0);
    });
  });

  /* The topbar search links open the search dialog instead of navigating, so they
     expose its open state. The details toggle event fires on every open and close
     path (summary, Escape, overlay, close button, predictive search). */
  const searchDetails = root.querySelector(".header-navigation__search-modal-host details");
  if (searchDetails && searchTriggers.length) {
    searchDetails.addEventListener("toggle", () => {
      const state = searchDetails.hasAttribute("open") ? "true" : "false";
      searchTriggers.forEach((trigger) => trigger.setAttribute("aria-expanded", state));
    });
  }
};

const initHeaderNavigationMobile = (root, closeDesktop) => {
  const drawer = root.querySelector("[data-mobile-drawer]");
  const overlay = root.querySelector("[data-mobile-drawer-overlay]");
  const toggle = root.querySelector("[data-mobile-drawer-toggle]");
  const closeButton = root.querySelector("[data-mobile-drawer-close]");
  const categoryButtons = Array.from(root.querySelectorAll("[data-mobile-category-toggle]"));
  const cartTrigger = root.querySelector("#cart-icon-bubble");

  if (!drawer || !overlay || !toggle) return;

  const searchTriggers = Array.from(drawer.querySelectorAll("[data-header-navigation-search-trigger]"));

  document.body.appendChild(overlay);
  document.body.appendChild(drawer);

  /* header-navigation.css skips rendering the closed drawer's contents during
     load (content-visibility). Once the page has loaded and gone idle, let the
     still-hidden drawer render so the first tap on the burger opens it without
     paying for its style and layout. */
  const prepareDrawer = () => drawer.classList.add("header-navigation__mobile-drawer--prepared");
  const schedulePrepareDrawer = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(prepareDrawer, { timeout: 2000 });
    } else {
      window.setTimeout(prepareDrawer, 1000);
    }
  };
  if (document.readyState === "complete") {
    schedulePrepareDrawer();
  } else {
    window.addEventListener("load", schedulePrepareDrawer, { once: true });
  }

  let isOpen = false;
  let suppressNextOutsideClick = false;
  /* The page-wide half of the open state (scroll lock and the hb-nav-drawer-open root class) is applied
     after the drawer has painted open and released after it has painted closed (see
     afterHeaderNavigationPaint). The token drops a pending half when the drawer is toggled again first. */
  let pageStateApplied = false;
  let pageStateToken = 0;

  const applyPageState = () => {
    if (pageStateApplied) return;
    pageStateApplied = true;
    lockHeaderNavigationScroll();
    // Launchers stand down on this root class (ui-overlay-system.css).
    document.documentElement.classList.add("hb-nav-drawer-open");
  };

  const releasePageState = () => {
    if (!pageStateApplied) return;
    pageStateApplied = false;
    document.documentElement.classList.remove("hb-nav-drawer-open");
    unlockHeaderNavigationScroll();
  };

  const showOverlay = () => {
    overlay.removeAttribute("hidden");
    overlay.classList.add("is-visible");
  };

  const hideOverlay = () => {
    overlay.classList.remove("is-visible");
    overlay.setAttribute("hidden", "");
  };

  const isOutsideDrawerTarget = (target) =>
    target instanceof Node && !drawer.contains(target) && !toggle.contains(target);

  const blockOutsideEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  const clearMobilePanelTimers = (panel) => {
    if (!panel) return;

    if (panel._hideTimer) {
      window.clearTimeout(panel._hideTimer);
      panel._hideTimer = null;
    }

    if (panel._resetTimer) {
      window.clearTimeout(panel._resetTimer);
      panel._resetTimer = null;
    }
  };

  const hideMobileCategoryPanel = (panel, immediate = false) => {
    if (!panel) return;

    clearMobilePanelTimers(panel);

    if (immediate || panel.hidden) {
      panel.classList.remove("is-open");
      panel.hidden = true;
      panel.style.height = "";
      return;
    }

    panel.style.height = `${panel.scrollHeight}px`;

    window.requestAnimationFrame(() => {
      panel.classList.remove("is-open");
      panel.style.height = "0px";
    });

    panel._hideTimer = window.setTimeout(() => {
      panel.hidden = true;
      panel.style.height = "";
      panel._hideTimer = null;
    }, HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS);
  };

  const showMobileCategoryPanel = (panel) => {
    if (!panel) return;

    clearMobilePanelTimers(panel);
    panel.hidden = false;
    panel.style.height = "0px";
    panel.classList.add("is-open");

    window.requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    panel._resetTimer = window.setTimeout(() => {
      if (panel.classList.contains("is-open")) {
        panel.style.height = "auto";
      }
      panel._resetTimer = null;
    }, HEADER_NAVIGATION_MOBILE_PANEL_TRANSITION_MS);
  };

  const collapseCategories = ({ immediate = false, exceptPanelId = "" } = {}) => {
    categoryButtons.forEach((button) => {
      const category = button.closest(".header-navigation__mobile-category");
      const panelId = button.dataset.mobileCategoryToggle;
      const panel = drawer.querySelector(`#${panelId}`);

      if (exceptPanelId && panelId === exceptPanelId) return;

      button.setAttribute("aria-expanded", "false");
      category?.classList.remove("is-open");
      hideMobileCategoryPanel(panel, immediate);
    });
  };

  const closeDrawer = (restoreFocus = true) => {
    if (!isOpen) return;

    isOpen = false;
    pageStateToken += 1;
    hideOverlay();
    drawer.querySelectorAll("localization-form").forEach((localizationForm) => {
      if (typeof localizationForm.hidePanel === "function") {
        localizationForm.hidePanel();
      }
    });
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    drawer.setAttribute("inert", "");
    toggle.setAttribute("aria-expanded", "false");
    collapseCategories({ immediate: true });
    // The trap's key handler goes now (a drawer opened by this same tap may set its own trap).
    if (typeof removeTrapFocus === "function") removeTrapFocus();
    // Focus goes back to the toggle in this task, as before: the drawer is inert now, so waiting for the
    // paint would leave focus on <body> in between (a screen reader would announce the page). preventScroll
    // because the scroll position is restored with the unlock below; the toggle sits in the sticky header.
    if (restoreFocus) toggle.focus({ preventScroll: true });

    /* Root class and scroll unlock (window.scrollTo forces a layout), once the closed drawer has painted. */
    const token = pageStateToken;
    afterHeaderNavigationPaint(() => {
      if (isOpen || token !== pageStateToken) return;
      releasePageState();
    });
  };

  const openDrawer = () => {
    if (isHeaderNavigationDesktop(root) || isOpen) return;

    isOpen = true;
    pageStateToken += 1;
    closeDesktop();
    showOverlay();
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    drawer.removeAttribute("inert");
    toggle.setAttribute("aria-expanded", "true");

    // Scroll lock, root class and focus once the open drawer has painted (focus waited 20 ms before).
    const token = pageStateToken;
    afterHeaderNavigationPaint(() => {
      if (!isOpen || token !== pageStateToken) return;
      applyPageState();
      const focusTarget = closeButton || drawer;
      if (typeof trapFocus === "function") {
        trapFocus(drawer, focusTarget);
        return;
      }
      focusTarget.focus();
    });
  };

  toggle.addEventListener("click", () => {
    if (isOpen) {
      closeDrawer(true);
    } else {
      openDrawer();
    }
  });

  closeButton?.addEventListener("click", () => closeDrawer(true));
  overlay.addEventListener("click", () => closeDrawer(true));

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (!isOpen || isHeaderNavigationDesktop(root)) return;
      if (!isOutsideDrawerTarget(event.target)) return;

      suppressNextOutsideClick = true;
      blockOutsideEvent(event);
      closeDrawer(true);
    },
    true
  );

  document.addEventListener(
    "click",
    (event) => {
      const shouldSuppressClick = suppressNextOutsideClick && isOutsideDrawerTarget(event.target);

      if (!isOpen && !shouldSuppressClick) return;
      if (!isOutsideDrawerTarget(event.target)) return;

      suppressNextOutsideClick = false;
      blockOutsideEvent(event);

      if (isOpen) {
        closeDrawer(true);
      }
    },
    true
  );

  categoryButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const category = button.closest(".header-navigation__mobile-category");
      const panelId = button.dataset.mobileCategoryToggle;
      const panel = drawer.querySelector(`#${panelId}`);
      const willOpen = button.getAttribute("aria-expanded") !== "true";

      collapseCategories({ exceptPanelId: willOpen ? panelId : "" });

      if (willOpen && panel) {
        button.setAttribute("aria-expanded", "true");
        category?.classList.add("is-open");
        showMobileCategoryPanel(panel);
      }
    });
  });

  document.addEventListener("keydown", (event) => {
    if ((event.code || event.key || "").toUpperCase() === "ESCAPE" && isOpen) {
      event.preventDefault();
      closeDrawer(true);
    }
  });

  cartTrigger?.addEventListener("click", () => {
    if (isOpen) {
      closeDrawer(false);
    }
  });

  searchTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      closeDesktop();

      const shouldWaitForDrawer = isOpen;
      if (isOpen) {
        closeDrawer(false);
      }

      window.setTimeout(() => {
        openHeaderNavigationSearchModal(root, trigger);
      }, shouldWaitForDrawer ? 300 : 0);
    });
  });

  document.addEventListener("country-change-modal:open", () => {
    if (isOpen) {
      closeDrawer(false);
    }
  });

  window.addEventListener("resize", () => {
    if (isHeaderNavigationDesktop(root)) {
      closeDrawer(false);
    }
  });

  initHeaderNavigationMobilePageLinks(drawer, closeDrawer);
};

// Measure every category together, including hidden menus, so switching categories
// never moves the product column. Re-measure after the storefront fonts load.
const syncHeaderNavigationRailWidth = (root) => {
  if (!isHeaderNavigationDesktop(root)) return;

  const measurement = document.createElement("div");
  measurement.setAttribute("aria-hidden", "true");
  measurement.inert = true;
  measurement.style.cssText = "position:fixed;visibility:hidden;pointer-events:none;width:max-content;";
  root.querySelectorAll(".header-navigation__mega-links").forEach((links) => {
    const copy = links.cloneNode(true);
    copy.style.cssText = "width:max-content;max-width:none;overflow:visible;";
    copy.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
    measurement.appendChild(copy);
  });
  root.appendChild(measurement);
  const width = Math.max(0, ...Array.from(measurement.children, (links) => links.getBoundingClientRect().width));
  measurement.remove();
  if (width) {
    // Include the rail's 10px gap and room for the 6px scrollbar a long list gets, so labels stay on
    // one line, retaining the desktop width cap.
    root.style.setProperty("--header-navigation-shared-rail-width", `${Math.min(360, Math.ceil(width) + 10 + 8)}px`);
  }
};

const initHeaderAccountLoyalty = (panel) => {
  if (!panel.dataset.customerId) return { refresh: () => {} };
  const api = () => window.HbOkendoLoyalty;
  const current = () => panel.isConnected && api()?.getConfig().customerId === panel.dataset.customerId;
  const copy = JSON.parse(panel.querySelector('[data-account-strings]').textContent);
  Object.keys(copy).forEach((key) => {
    const text = document.createElement('textarea');
    text.innerHTML = copy[key];
    copy[key] = text.value;
  });
  const rewards = panel.querySelector('[data-account-rewards]');
  const enrol = panel.querySelector('[data-account-enrol]');
  const notice = panel.querySelector('[data-account-status]');
  const retry = panel.querySelector('[data-account-retry]');
  const spinner = panel.querySelector('[data-account-loading]');
  const setBusy = (busy) => {
    if (spinner) spinner.hidden = !busy;
    panel.setAttribute('aria-busy', String(busy));
  };
  let customer = null;
  let jwt = '';
  let loading = false;
  let joining = false;
  let enrolPromise = null;
  const message = (text, failed = false) => {
    notice.textContent = text;
    notice.hidden = !text;
    retry.hidden = !failed;
  };
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    node.textContent = text || '';
    if (className) node.className = className;
    return node;
  };
  const renderEnrolment = async () => {
    if (!current() || customer?.status !== 'pending' || joining || enrol.dataset.ready || enrolPromise) return enrolPromise;
    enrolPromise = (async () => {
      const policies = await api().getLegalPolicies();
      if (!current() || customer?.status !== 'pending') return;
      if (!policies.version || !policies.policySentence?.html || !policies.acceptTermsAndJoinProgramButtonText)
        throw new Error('Missing enrollment policies');
      const heading = element('h3', copy.joinHeading);
      const intro = element('p', copy.joinMessage);
      const consent = element('div', '', 'header-navigation__account-consent');
      const check = element('input');
      check.type = 'checkbox';
      check.id = `${panel.id}-LoyaltyConsent`;
      const terms = element('label');
      terms.htmlFor = check.id;
      terms.append(api().safePolicy(policies.policySentence.html));
      consent.append(check, terms);
      const button = element('button', policies.acceptTermsAndJoinProgramButtonText, 'button button--primary');
      button.type = 'button';
      button.disabled = true;
      check.addEventListener('change', () => { button.disabled = !check.checked || joining || !jwt; });
      button.addEventListener('click', async () => {
        if (!current() || joining || !check.checked || !jwt || customer?.status !== 'pending') return;
        joining = true;
        check.disabled = true;
        button.disabled = true;
        message('');
        try {
          await api().activateCustomer(jwt);
          if (!current()) return;
          await refresh();
        } catch {
          if (current()) message(copy.error, true);
        } finally {
          joining = false;
          check.disabled = false;
          button.disabled = !check.checked || !jwt || customer?.status !== 'pending';
        }
      });
      enrol.replaceChildren(heading, intro, consent, button);
      enrol.dataset.ready = 'true';
      enrol.hidden = false;
    })();
    try { await enrolPromise; } finally { enrolPromise = null; }
  };
  const showCustomer = (snapshot) => {
    if (!current() || snapshot?.customerId !== panel.dataset.customerId) return;
    customer = snapshot.customer;
    const enrolled = customer.status === 'enrolled';
    if (enrolled) setBusy(false);
    const balance = customer.points?.balance;
    rewards.hidden = !enrolled || balance == null;
    if (balance != null) {
      const formatted = new Intl.NumberFormat(panel.dataset.languageCode || 'en', { numberingSystem: 'latn' }).format(balance);
      panel.querySelector('[data-account-points]').textContent = window.hbNumerals ? window.hbNumerals.number(formatted) : formatted;
    }
    const tier = panel.querySelector('[data-account-tier]');
    if (customer.vipTierName) {
      // Gold, Silver or Bronze like the Loyalty tab (snippets/account-tier-label.liquid); the pill colour keys off Okendo's own name.
      const name = customer.vipTierName.trim().toLowerCase().replace(/\s+/g, ' ');
      const word = name.includes('gold') ? tier.dataset.tierGolden : name.includes('silver') ? tier.dataset.tierSilver : name.includes('bronz') ? tier.dataset.tierBronzed : '';
      tier.textContent = word || customer.vipTierName.trim();
      rewards.dataset.tierName = name;
    }
    if (!enrolled) tier.textContent = '';
    tier.hidden = !enrolled || !tier.textContent.trim();
    if (tier.hidden) rewards.dataset.tierName = '';
    const tierGroup = panel.querySelector('[data-account-tier-group]');
    const divider = panel.querySelector('[data-account-rewards-divider]');
    if (tierGroup) tierGroup.hidden = tier.hidden;
    if (divider) divider.hidden = tier.hidden;
    if (enrolled || customer.status !== 'pending') {
      enrol.hidden = true;
      enrol.replaceChildren();
      delete enrol.dataset.ready;
    }
    if (customer.status === 'blocked') message(copy.blocked);
  };
  const refresh = async () => {
    if (!current() || loading) return;
    showCustomer(api().getCustomerSnapshot());
    loading = true;
    setBusy((!customer && rewards.hidden) || (customer?.status === 'pending' && !enrol.dataset.ready));
    message('');
    try {
      const session = await api().ensureOkendoSession({ force: true });
      if (!current()) return;
      jwt = session.jwt;
      // The coupon response can carry the current tier even when customer_details omits it.
      const details = session.customerDetails.loyaltyCustomer;
      if (details.status?.toLowerCase() === 'enrolled' && !details.vipTierName) {
        const coupons = await api().getActiveCoupons(jwt).catch(() => ({}));
        if (!current()) return;
        if (coupons.vipTierName) details.vipTierName = coupons.vipTierName;
        // Only the tier is new here: points may have changed while coupons loaded (a redemption elsewhere on the
        // page), so the latest published balance is kept.
        api().updateCustomerDisplay({ ...details, points: api().getCustomerSnapshot()?.customer.points || details.points }, jwt);
      }
      showCustomer(api().getCustomerSnapshot());
      setBusy(customer?.status === 'pending' && !enrol.dataset.ready);
      if (customer?.status === 'pending') await renderEnrolment();
      if (current()) message(customer?.status === 'blocked' ? copy.blocked : '');
    } catch {
      if (current()) message(rewards.hidden ? copy.error : '', rewards.hidden);
    } finally {
      loading = false;
      setBusy(false);
    }
  };
  document.addEventListener('hb:loyalty:customer-updated', (event) => {
    showCustomer(event.detail);
  });
  retry.addEventListener('click', () => { if (!joining) void refresh(); });
  return { refresh };
};

const initHeaderAccountPanel = (root, closeNavigation) => {
  const panel = root.querySelector('[data-header-account-panel]');
  const triggers = Array.from(root.querySelectorAll('[data-header-account-trigger]'));
  if (!panel || !triggers.length) return;
  const loyalty = initHeaderAccountLoyalty(panel);
  let activeTrigger = null;
  const close = (restoreFocus = false) => {
    panel.hidden = true;
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (restoreFocus) activeTrigger?.focus();
  };
  triggers.forEach((trigger) => {
    trigger.setAttribute('role', 'button');
    trigger.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (!panel.hidden) { close(); return; }
      const searchModal = root.querySelector('.header-navigation__search-modal-host details-modal');
      if (searchModal?.isOpen()) searchModal.close(false);
      closeNavigation();
      activeTrigger = trigger;
      panel.hidden = false;
      void loyalty.refresh();
      triggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      const target = trigger.getBoundingClientRect();
      const bounds = panel.getBoundingClientRect();
      const arrow = Math.max(14, Math.min(bounds.width - 26, target.left + target.width / 2 - bounds.left - 6));
      panel.style.setProperty('--account-arrow-left', `${arrow}px`);
      // The main actions (Login / My account) keep first focus, not the program link above them.
      (panel.querySelector('.header-navigation__account-actions a') || panel.querySelector('a'))?.focus();
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === ' ') { event.preventDefault(); trigger.click(); }
    });
  });
  document.addEventListener('click', (event) => {
    if (!panel.hidden && !panel.contains(event.target) && !triggers.some((trigger) => trigger.contains(event.target))) close();
  });
  document.addEventListener('focusin', (event) => {
    if (!panel.hidden && !panel.contains(event.target) && !triggers.includes(event.target)) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); close(true); }
  });
  root.addEventListener('mouseenter', (event) => {
    if (event.target.matches('.header-navigation__primary-link, [data-secondary-trigger], [data-mega-link-trigger]')) close();
  }, true);
  // Also cover drawers opened through code, without a click outside the widget.
  document.addEventListener('hb:cart-drawer:open', () => close());
  document.addEventListener('country-change-modal:open', () => close());
  const navigationObserver = new MutationObserver((changes) => {
    if (panel.hidden) return;
    const otherPanelOpened = changes.some(({ target }) =>
      !target.hasAttribute('data-header-account-trigger') && !panel.contains(target) &&
      (target.getAttribute('aria-expanded') === 'true' || target.hasAttribute('open'))
    );
    if (otherPanelOpened) close();
  });
  navigationObserver.observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-expanded', 'open'] });
  panel.querySelector('[data-header-account-logout]')?.addEventListener('click', () => {
    if (typeof window.DLLogout === 'function') window.DLLogout();
  });
  // "My account" on the account page itself closes the panel instead of reloading the page (the same-page rule of the
  // header icons, below), focus back on the account icon. The icon keeps opening the panel there: it opens a panel
  // rather than a page, and the panel is where a signed-in shopper finds Log out. Sub-pages (/account/orders/...)
  // navigate.
  const home = panel.querySelector('[data-header-account-home]');
  if (home && isHeaderNavigationCurrentPage(home)) home.setAttribute('aria-current', 'page');
  home?.addEventListener('click', (event) => {
    if (!isHeaderNavigationSamePageClick(event, home)) return;
    event.preventDefault();
    close(true);
  });
  window.addEventListener('resize', () => close());
  return { close };
};

/* Plain page links: the header icons (VIPs, Wishlist) and the links in the phone menu (VIPs, My account, the menu
   links). Not the cart, search or account triggers that open a panel. On their own page a click does nothing (no
   reload, no scroll jump) and the link carries aria-current="page"; in the phone menu it closes the menu, as the
   close button does. Same page = same origin and path (trailing slash ignored; the locale prefix compared
   without case, and a link without one lands on the current market) with no content query on either URL.
   So /pages/wishlist?share=<token>, the shared read-only list, still opens the shopper's own list.
   Only preview, tracking and ad click parameters are ignored. */
const HEADER_NAVIGATION_PLAIN_LINK =
  "[href]:not([data-header-account-trigger]):not([data-header-navigation-search-trigger]):not(.header-navigation__icon-button--cart):not([aria-haspopup]):not([aria-controls])";
const HEADER_NAVIGATION_PAGE_ICON_SELECTOR = `.header-navigation__icon-button${HEADER_NAVIGATION_PLAIN_LINK}`;
const HEADER_NAVIGATION_MOBILE_LINK_SELECTOR = `a${HEADER_NAVIGATION_PLAIN_LINK}`;
const HEADER_NAVIGATION_IGNORED_PARAM = /^(preview_theme_id|_pos|_sid|_ss|_psq|_fid|_ab|_bt|_fd|_sc|pb|_kx|oseid|gclid|gbraid|wbraid|fbclid|ttclid|msclkid|srsltid|utm_[a-z_]+)$/i;
const HEADER_NAVIGATION_LOCALE_PREFIX = /^\/[a-z]{2}(?:-[a-z]{2,4})?(?=\/|$)/i;

const splitHeaderNavigationPath = (pathname) => {
  const path = pathname.replace(/\/+$/, "");
  const locale = (path.match(HEADER_NAVIGATION_LOCALE_PREFIX) || [""])[0];
  return { locale: locale.toLowerCase(), page: path.slice(locale.length) || "/" };
};

const hasHeaderNavigationContentQuery = (url) =>
  Array.from(url.searchParams.keys()).some((key) => !HEADER_NAVIGATION_IGNORED_PARAM.test(key));

const isHeaderNavigationCurrentPage = (link) => {
  const href = link.getAttribute("href");
  // A link with its own #fragment (or a bare "#") means "jump there": leave it to the browser.
  if (!href || href.includes("#")) return false;

  try {
    const target = new URL(href, window.location.href);
    const current = new URL(window.location.href);
    if (target.origin !== current.origin || hasHeaderNavigationContentQuery(target) || hasHeaderNavigationContentQuery(current)) {
      return false;
    }
    const to = splitHeaderNavigationPath(target.pathname);
    const at = splitHeaderNavigationPath(current.pathname);
    // A link without a locale prefix lands on the current market. A link with one names its market, which must be the
    // one this page is in: a URL without a prefix is in the root market (Shopify.routes.root, "/" for the primary
    // one), never in every market (/fr/pages/wishlist from /pages/wishlist navigates).
    const here = at.locale || splitHeaderNavigationPath(window.Shopify?.routes?.root || "/").locale;
    return to.page === at.page && (!to.locale || to.locale === here);
  } catch (_) {
    return false;
  }
};

// True when this click on `link` should do nothing because the shopper is already on that page. Enter on a focused
// link fires the same click. Modified and middle clicks keep the browser default (new tab or window), as do links
// that target another window or download.
const isHeaderNavigationSamePageClick = (event, link) => {
  if (!link || event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if ((link.target && link.target !== "_self") || link.hasAttribute("download")) return false;
  return isHeaderNavigationCurrentPage(link);
};

const initHeaderNavigationCurrentPageIcons = (root, closeDesktop) => {
  root.querySelectorAll(HEADER_NAVIGATION_PAGE_ICON_SELECTOR).forEach((link) => {
    if (isHeaderNavigationCurrentPage(link)) {
      link.setAttribute("aria-current", "page");
    } else {
      link.removeAttribute("aria-current");
    }
  });

  root.addEventListener("click", (event) => {
    const link = event.target.closest?.(HEADER_NAVIGATION_PAGE_ICON_SELECTOR);
    if (!isHeaderNavigationSamePageClick(event, link)) return;

    event.preventDefault();
    closeDesktop();
  });
};

// The phone menu (the drawer is moved to <body>, so the header root never sees its clicks): a link to the page the
// shopper is on closes the menu with its normal focus return and does not navigate. Links the markup already marks
// aria-current (menu links) are never unmarked; the rest get it only when they point at this page.
const initHeaderNavigationMobilePageLinks = (drawer, closeDrawer) => {
  drawer.querySelectorAll(HEADER_NAVIGATION_MOBILE_LINK_SELECTOR).forEach((link) => {
    if (isHeaderNavigationCurrentPage(link)) link.setAttribute("aria-current", "page");
  });

  drawer.addEventListener("click", (event) => {
    const link = event.target.closest?.(HEADER_NAVIGATION_MOBILE_LINK_SELECTOR);
    if (!isHeaderNavigationSamePageClick(event, link)) return;

    event.preventDefault();
    closeDrawer(true);
  });
};

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-header-navigation]").forEach((headerRoot) => {
    const mobileDrawer = headerRoot.querySelector(".header-navigation__mobile-drawer");
    const mobileOverlay = headerRoot.querySelector(".header-navigation__mobile-overlay");
    let desktop = { closeAll: () => {} };
    let isDesktopInitialized = false;
    let isMobileInitialized = false;
    let syncStickyMetrics = () => {};

    const closeDesktop = () => desktop.closeAll();

    const initDesktopIfNeeded = () => {
      if (isDesktopInitialized || !isHeaderNavigationDesktop(headerRoot)) return;

      syncHeaderNavigationRailWidth(headerRoot);
      document.fonts.ready.then(() => syncHeaderNavigationRailWidth(headerRoot));
      desktop = initHeaderNavigationDesktop(headerRoot);
      isDesktopInitialized = true;
    };

    const initMobileIfNeeded = () => {
      if (isMobileInitialized || isHeaderNavigationDesktop(headerRoot)) return;

      initHeaderNavigationMobile(headerRoot, closeDesktop);
      isMobileInitialized = true;
    };

    const syncNavigationMode = () => {
      initDesktopIfNeeded();
      initMobileIfNeeded();
    };

    initHeaderAccountPanel(headerRoot, closeDesktop);

    // Measure the desktop candidate even after a resize selected mobile mode.
    // This lets a wider viewport restore desktop without a guessed breakpoint.
    const fitHeader = () => {
      headerRoot.classList.remove("is-mobile-layout");
      if (isHeaderNavigationDesktopWidth()) {
        const wasCompact = headerRoot.classList.contains("is-sticky-compact");
        headerRoot.classList.remove("is-sticky-compact");
        const primary = headerRoot.querySelector(".header-navigation__primary-list")?.getBoundingClientRect();
        const logo = headerRoot.querySelector(".header-navigation__logo").getBoundingClientRect();
        const controls = Array.from(headerRoot.querySelectorAll(".header-navigation__icons > *"))
          .filter((el) => el.offsetWidth).map((el) => el.getBoundingClientRect());
        const isRtl = getComputedStyle(headerRoot).direction === "rtl";
        const expandedOverlaps = isRtl
          ? (primary && primary.left - 16 < logo.right) || controls.some((rect) => rect.right > logo.left - 16)
          : (primary && primary.right + 16 > logo.left) || controls.some((rect) => rect.left < logo.right + 16);
        const wasTight = headerRoot.classList.contains("is-sticky-compact-tight");
        headerRoot.classList.add("is-sticky-compact");
        const icons = headerRoot.querySelector(".header-navigation__icons");
        headerRoot.style.setProperty("--header-navigation-icons-space", `${Math.ceil(icons.offsetWidth) + 40}px`);
        const nav = Array.from(headerRoot.querySelectorAll(".header-navigation__secondary-nav")).find((el) => el.offsetParent);
        const style = nav && getComputedStyle(nav);
        const available = nav ? nav.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) : 0;
        // Measure the labels at the smaller type fitCompactNav() falls back to, so the desktop header is kept
        // wherever the compact bar can fit. Include hidden categories so switching menus cannot cause overlap.
        headerRoot.classList.add("is-sticky-compact-tight");
        const needed = nav ? Math.max(0, ...Array.from(headerRoot.querySelectorAll(".header-navigation__secondary-list"), (list) => {
          const copy = list.cloneNode(true);
          copy.setAttribute("aria-hidden", "true");
          copy.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
          copy.style.cssText = "position:absolute;visibility:hidden;width:max-content;pointer-events:none;";
          nav.appendChild(copy);
          const width = Array.from(copy.children).reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) + Math.max(0, copy.children.length - 1) * 12 + 16;
          copy.remove();
          return width;
        })) : 0;
        headerRoot.classList.toggle("is-sticky-compact-tight", wasTight);
        headerRoot.classList.toggle("is-sticky-compact", wasCompact);
        headerRoot.classList.toggle("is-mobile-layout", expandedOverlaps || needed > available);
      }
      const mobileLayout = headerRoot.classList.contains("is-mobile-layout");
      headerRoot.closest(".header-navigation-section")?.classList.toggle("is-mobile-layout", mobileLayout);
      mobileDrawer?.classList.toggle("is-mobile-layout", mobileLayout);
      mobileOverlay?.classList.toggle("is-mobile-layout", mobileLayout);
      if (!isHeaderNavigationDesktop(headerRoot)) closeDesktop();
      syncNavigationMode();
      syncStickyMetrics();
    };
    fitHeader();
    window.addEventListener("resize", fitHeader);
    document.fonts.ready.then(fitHeader);

    syncStickyMetrics = initHeaderNavigationSticky(headerRoot, closeDesktop) || (() => {});
    initHeaderNavigationSearch(headerRoot, closeDesktop);
    syncNavigationMode();

    headerRoot.querySelectorAll(".header-navigation__icon-button--cart, .header-navigation__icon-button--search").forEach((link) => {
      link.addEventListener("click", () => {
        closeDesktop();
      });
    });
    initHeaderNavigationCurrentPageIcons(headerRoot, closeDesktop);
  });
});
